import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine
} from 'recharts';
import { MdSensors, MdOutlineSignalCellularAlt, MdArrowForwardIos } from 'react-icons/md';
import { format, subDays } from 'date-fns';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const COLORS = [
    '#4f8ef7', '#22c55e', '#f59e0b', '#a78bfa', '#f472b6',
    '#38bdf8', '#fb923c', '#34d399', '#e879f9', '#60a5fa'
];

const MiniTooltip = ({ active, payload, unit }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '6px 10px', fontSize: 12, pointerEvents: 'none'
        }}>
            <span style={{ color: 'var(--text-secondary)', marginRight: 6 }}>{payload[0]?.payload?.time}</span>
            <strong style={{ color: 'var(--primary)' }}>{payload[0]?.value}</strong>
            {unit && <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>{unit}</span>}
        </div>
    );
};

const SensorCard = ({ sensor, chartData, loading, colorIdx, onNavigate }) => {
    const color = COLORS[colorIdx % COLORS.length];
    const lastVal = chartData.length > 0 ? chartData[chartData.length - 1].value : null;

    const values = chartData.map(d => d.value).filter(v => v !== null && v !== undefined && !isNaN(v));
    const minVal = values.length ? Math.min(...values) : null;
    const maxVal = values.length ? Math.max(...values) : null;
    const avgVal = values.length ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1) : null;

    return (
        <motion.div
            className="card"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            style={{ cursor: 'pointer', transition: 'border-color 0.2s' }}
            onClick={() => onNavigate(sensor.sensorUUID)}
            onMouseEnter={e => e.currentTarget.style.borderColor = color}
            onMouseLeave={e => e.currentTarget.style.borderColor = ''}
        >
            {/* Card Header */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                padding: '14px 16px 10px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                        background: `${color}22`, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', color
                    }}>
                        <MdSensors size={18} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{
                            fontWeight: 700, fontSize: 16, color: 'var(--text-primary)',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            maxWidth: 200
                        }}>
                            {sensor.sensorName}
                        </div>
                        {sensor.unit && (
                            <div style={{
                                fontSize: 11, fontWeight: 600, color, marginTop: 3,
                                background: `${color}18`, display: 'inline-block',
                                padding: '2px 7px', borderRadius: 4
                            }}>
                                {sensor.unit}
                            </div>
                        )}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {lastVal !== null && (
                        <span style={{
                            fontFamily: 'monospace', fontWeight: 800, fontSize: 16, color
                        }}>
                            {lastVal}
                        </span>
                    )}
                    <MdArrowForwardIos size={12} style={{ color: 'var(--text-muted)' }} />
                </div>
            </div>

            {/* Chart */}
            <div style={{ padding: '0 4px 4px', height: 155 }}>
                {loading ? (
                    <div style={{
                        height: '100%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div className="spinner" style={{ width: 20, height: 20 }} />
                    </div>
                ) : chartData.length === 0 ? (
                    <div style={{
                        height: '100%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 12, color: 'var(--text-muted)'
                    }}>
                        Veri yok
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 4, bottom: 0 }}>
                            <defs>
                                <linearGradient id={`grad-${sensor.sensorUUID}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" />
                            <XAxis
                                dataKey="time"
                                tick={{ fill: 'var(--text-secondary)', fontSize: 11, fontWeight: 500 }}
                                tickLine={{ stroke: 'var(--border)', strokeWidth: 1 }}
                                axisLine={{ stroke: 'var(--border)', strokeWidth: 1 }}
                                interval="preserveStartEnd"
                                minTickGap={40}
                            />
                            <YAxis
                                tick={{ fill: 'var(--text-secondary)', fontSize: 11, fontWeight: 500 }}
                                tickLine={{ stroke: 'var(--border)', strokeWidth: 1 }}
                                axisLine={{ stroke: 'var(--border)', strokeWidth: 1 }}
                                width={42}
                            />
                            <Tooltip content={<MiniTooltip unit={sensor.unit} />} />
                            <Area
                                type="monotone" dataKey="value"
                                stroke={color} strokeWidth={2}
                                fill={`url(#grad-${sensor.sensorUUID})`}
                                dot={false}
                                activeDot={{ r: 4, fill: color, stroke: 'white', strokeWidth: 2 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* Min / Avg / Max Stats */}
            {values.length > 0 && (
                <div style={{
                    display: 'flex', justifyContent: 'space-around',
                    padding: '8px 12px 12px',
                    borderTop: '1px solid var(--border)',
                    marginTop: 2
                }}>
                    {[
                        { label: 'Min', val: minVal, clr: '#38bdf8' },
                        { label: 'Avg', val: avgVal, clr: color },
                        { label: 'Max', val: maxVal, clr: '#f472b6' }
                    ].map(({ label, val, clr }) => (
                        <div key={label} style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                                {label}
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: clr, fontFamily: 'monospace' }}>
                                {val}{sensor.unit ? <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 2 }}>{sensor.unit}</span> : ''}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </motion.div>
    );
};

export default function CompanyDataSignalsPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useLanguage();

    const [company, setCompany] = useState(null);
    const [companyLoading, setCompanyLoading] = useState(true);
    const [sensorData, setSensorData] = useState({});
    const [dataLoading, setDataLoading] = useState(false);

    const [startDate, setStartDate] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [activeRange, setActiveRange] = useState(1);

    // Load company structure once
    useEffect(() => {
        if (!user?.companyId) return;
        setCompanyLoading(true);
        api.get(`/companies/${user.companyId}`)
            .then(res => setCompany(res.data.data))
            .catch(() => toast.error(t('companyDetail.dataLoadFailed')))
            .finally(() => setCompanyLoading(false));
    }, [user, t]);

    // Fetch all sensor time-series data whenever dates change
    const fetchAllSensorData = useCallback(async () => {
        if (!company?.systems) return;
        const allSensors = company.systems.flatMap(s => s.sensors || []);
        if (allSensors.length === 0) return;

        setDataLoading(true);
        const results = await Promise.allSettled(
            allSensors.map(sensor =>
                api.get(`/sensor-data/${sensor.sensorUUID}`, {
                    params: { startDate, endDate }
                }).then(res => ({
                    uuid: sensor.sensorUUID,
                    data: (res.data.data || []).map(d => ({
                        time: format(new Date(d.timestamp), 'MM/dd HH:mm'),
                        value: d.value
                    }))
                }))
            )
        );

        const map = {};
        results.forEach(r => {
            if (r.status === 'fulfilled') {
                map[r.value.uuid] = r.value.data;
            }
        });
        setSensorData(map);
        setDataLoading(false);
    }, [company, startDate, endDate]);

    useEffect(() => {
        fetchAllSensorData();
    }, [fetchAllSensorData]);

    const setQuickRange = (days) => {
        setActiveRange(days);
        const end = new Date();
        const start = subDays(end, days);
        setStartDate(format(start, 'yyyy-MM-dd'));
        setEndDate(format(end, 'yyyy-MM-dd'));
    };

    const allSystems = company?.systems?.filter(s => (s.sensors || []).length > 0) || [];
    let globalColorIdx = 0;

    if (companyLoading) {
        return (
            <div className="loading-page">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            {/* Page Header */}
            <div className="page-header">
                <div className="page-header-left">
                    <div style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        background: 'rgba(79,142,247,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--primary)'
                    }}>
                        <MdOutlineSignalCellularAlt size={22} />
                    </div>
                    <div>
                        <h1 className="page-title">{t('dataSignals.title')}</h1>
                        <p className="page-subtitle">{t('dataSignals.subtitle')}</p>
                    </div>
                </div>
            </div>

            {/* Date Range Filter */}
            <div className="filter-bar" style={{ marginBottom: 24 }}>
                <span className="filter-label">📅 {t('sensorDetail.dateRange')}:</span>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('sensorDetail.startDate')}
                    </span>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </label>
                <span style={{ color: 'var(--text-muted)', alignSelf: 'flex-end', paddingBottom: 6 }}>→</span>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('sensorDetail.endDate')}
                    </span>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </label>
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={fetchAllSensorData}
                    style={{ alignSelf: 'flex-end' }}
                    disabled={dataLoading}
                >
                    {dataLoading ? <span className="spinner spinner-sm" /> : t('common.filter')}
                </button>

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignSelf: 'flex-end' }}>
                    {[
                        { days: 1, label: t('sensorDetail.daily') },
                        { days: 7, label: t('sensorDetail.weekly') },
                        { days: 30, label: t('sensorDetail.monthly') }
                    ].map(({ days, label }) => (
                        <button
                            key={days}
                            className={`btn btn-sm ${activeRange === days ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setQuickRange(days)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Systems + Sensor Charts */}
            {allSystems.length === 0 ? (
                <div className="empty-state" style={{ marginTop: 40 }}>
                    <div className="empty-state-icon"><MdSensors size={48} /></div>
                    <div className="empty-state-title">{t('dataSignals.noSensors')}</div>
                    <div className="empty-state-desc">{t('dataSignals.noSensorsDesc')}</div>
                </div>
            ) : (
                allSystems.map(system => {
                    const sensors = system.sensors || [];
                    return (
                        <div key={system._id} style={{ marginBottom: 32 }}>
                            {/* System Header */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                marginBottom: 14
                            }}>
                                <div style={{
                                    width: 3, height: 20, background: 'var(--primary)',
                                    borderRadius: 2, flexShrink: 0
                                }} />
                                <h2 style={{
                                    margin: 0, fontSize: 16, fontWeight: 700,
                                    color: 'var(--text-primary)'
                                }}>
                                    {system.systemName}
                                </h2>
                                <span style={{
                                    fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
                                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                                    borderRadius: 20, padding: '2px 10px'
                                }}>
                                    {sensors.length} {t('common.sensor')}
                                </span>
                            </div>

                            {/* 2-Column Sensor Grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 16
                            }}
                                className="data-signals-grid"
                            >
                                {sensors.map(sensor => {
                                    const idx = globalColorIdx++;
                                    return (
                                        <SensorCard
                                            key={sensor.sensorUUID}
                                            sensor={sensor}
                                            chartData={sensorData[sensor.sensorUUID] || []}
                                            loading={dataLoading && !sensorData[sensor.sensorUUID]}
                                            colorIdx={idx}
                                            onNavigate={uuid => navigate(`/company/sensors/${uuid}`)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })
            )}

            <style>{`
                @media (max-width: 640px) {
                    .data-signals-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </div>
    );
}
